/* SW2 — banner de actualización CONTROLADO POR EL USUARIO.
 *
 * Dos situaciones, ninguna recarga sola:
 *   · hay una versión en espera (`orca-sw-update`): se ofrece "Actualizar". El
 *     clic censa las pestañas abiertas, avisa si algo se perdería, espera SIN
 *     TECHO a que acabe cualquier subida, activa el SW nuevo y solo entonces
 *     recarga. El usuario puede cancelar la espera.
 *   · otra pestaña ya activó la versión nueva (`orca-sw-controller-changed` sin
 *     que esta lo pidiera): esta pestaña SIGUE con su JS —el SW conserva sus
 *     bundles— y se ofrece "Recargar" cuando el usuario termine lo suyo.
 *
 * Presentación pura (inline styles, sin dependencias de UI).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { deferWhileUploading, hasUploadsInFlight } from "../lib/uploadsInFlight";
import { allowNextUnload, confirmLeave, unsavedWorkState } from "../lib/unsavedWork";
import {
  EVT_CONTROLLER_CHANGED, EVT_UPDATE_READY, getUpdateState, inspectOpenTabs, requestActivation,
} from "../lib/swUpdate";

const BTN = {
  background: "#4F46E5", color: "#fff", border: "none", borderRadius: 8,
  padding: "7px 14px", fontSize: 13, fontFamily: "inherit", fontWeight: 600, whiteSpace: "nowrap",
};

export default function UpdateBanner() {
  const [worker, setWorker] = useState(() => getUpdateState().waiting);
  const [appliedElsewhere, setAppliedElsewhere] = useState(() => getUpdateState().appliedElsewhere);
  const [phase, setPhase] = useState("idle");   // idle | checking | waiting-upload | activating | failed
  // Each click owns its callbacks. Cancelling must never re-arm an older waiter.
  const attempt = useRef(0);

  useEffect(() => {
    const onReady = (e) => setWorker((e.detail && e.detail.worker) || true);
    const onChanged = (e) => {
      if (e.detail && e.detail.requested) return;       // la pidió esta pestaña
      attempt.current += 1;
      setPhase("idle");
      setWorker(null);
      setAppliedElsewhere(true);
    };
    window.addEventListener(EVT_UPDATE_READY, onReady);
    window.addEventListener(EVT_CONTROLLER_CHANGED, onChanged);
    return () => {
      attempt.current += 1;
      window.removeEventListener(EVT_UPDATE_READY, onReady);
      window.removeEventListener(EVT_CONTROLLER_CHANGED, onChanged);
    };
  }, []);

  const reloadNow = useCallback(() => {
    allowNextUnload();
    window.location.reload();
  }, []);

  // Espera SIN techo a las subidas: tirar bytes del usuario es peor que tardar.
  const whenIdle = useCallback((token, go) => {
    if (hasUploadsInFlight()) setPhase("waiting-upload");
    deferWhileUploading(() => { if (attempt.current === token) go(); }, { ceilingMs: Infinity });
  }, []);

  const update = useCallback(async () => {
    const token = ++attempt.current;
    setPhase("checking");
    const tabs = worker && worker !== true ? await inspectOpenTabs({ worker }) : { legacyTabs: null };
    if (attempt.current !== token) return;
    const own = unsavedWorkState();
    if (own.dirty || (tabs.legacyTabs || 0) > 0) {
      const lines = ["Vas a actualizar Orca Deck."];
      if (own.dirty) {
        lines.push(own.localFiles.length
          ? `Esta pestaña tiene archivos locales sin subir que NO se pueden recuperar: ${own.localFiles.join(", ")}.`
          : "Esta pestaña tiene trabajo sin enviar; se guarda como borrador y reaparece al recargar.");
        if (own.storageFailed) lines.push("OJO: el borrador no se pudo guardar en este navegador, así que se perdería.");
      }
      if ((tabs.legacyTabs || 0) > 0) {
        lines.push(`Hay ${tabs.legacyTabs} pestaña(s) abierta(s) con una versión anterior de Orca Deck que se recargarán solas `
          + "al actualizar y perderán lo que no hayan enviado. Envía o cierra su trabajo antes.");
      }
      lines.push("¿Actualizar ahora?");
      if (!window.confirm(lines.join("\n\n"))) { setPhase("idle"); return; }
    }
    whenIdle(token, async () => {
      setPhase("activating");
      const ok = await requestActivation({ worker: worker === true ? null : worker });
      if (attempt.current !== token) return;
      if (ok) reloadNow();
      else setPhase("failed");
    });
  }, [worker, whenIdle, reloadNow]);

  const reloadElsewhere = useCallback(() => {
    if (!confirmLeave("actualizar")) return;
    const token = ++attempt.current;
    whenIdle(token, reloadNow);
  }, [whenIdle, reloadNow]);

  if (!worker && !appliedElsewhere) return null;

  const busy = phase === "checking" || phase === "activating" || phase === "waiting-upload";
  let text; let action; let label;
  if (appliedElsewhere) {
    text = phase === "waiting-upload"
      ? "Recargará en cuanto termine la subida en curso."
      : "Hay una versión nueva activa (se aplicó desde otra pestaña). Esta pestaña sigue con la anterior; recárgala cuando termines.";
    action = reloadElsewhere; label = phase === "waiting-upload" ? "Esperando…" : "Recargar";
  } else if (phase === "failed") {
    text = "No se pudo activar la versión nueva. Puedes recargar manualmente cuando quieras.";
    action = reloadElsewhere; label = "Recargar";
  } else {
    text = phase === "waiting-upload"
      ? "Nueva versión lista. Se actualizará en cuanto termine la subida en curso."
      : phase === "activating" ? "Activando la nueva versión…"
        : "Nueva versión disponible. No se recarga sola: actualiza cuando termines lo que tienes entre manos.";
    action = update;
    label = phase === "waiting-upload" ? "Al terminar…" : phase === "activating" ? "Actualizando…" : phase === "checking" ? "Comprobando…" : "Actualizar";
  }

  return (
    <div
      data-testid="sw-update-banner"
      role="status"
      style={{
        position: "fixed", left: 12, right: 12, bottom: 12, zIndex: 2000,
        maxWidth: 480, margin: "0 auto",
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 14px", borderRadius: 10,
        background: "#101017", color: "#E6E6EE",
        border: "1px solid #2A2A38",
        boxShadow: "0 8px 30px rgba(0,0,0,0.45)",
        fontSize: 13, fontFamily: "inherit",
      }}
    >
      <span style={{ fontSize: 16 }}>✨</span>
      <span style={{ flex: 1 }} data-testid="sw-update-text">{text}</span>
      {phase === "waiting-upload" && (
        <button
          data-testid="sw-update-cancel"
          onClick={() => { attempt.current += 1; setPhase("idle"); }}
          style={{ ...BTN, background: "transparent", border: "1px solid #3A3A4A", color: "#C8C8D4", cursor: "pointer" }}
        >Cancelar</button>
      )}
      <button
        data-testid="sw-update-reload"
        onClick={action}
        disabled={busy}
        style={{ ...BTN, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}
      >{label}</button>
    </div>
  );
}
